/** MeshAI floating assistant — PANEL light/dark (ModernDark surface hiyerarşisi) */

import { keyframes } from "@mui/material";
import { PANEL } from "./panelTokens";

export const panelAssistantPanelIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const panelAssistantFabPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 var(--panel-accent-focus-ring); }
  50% { box-shadow: 0 0 0 10px transparent; }
`;

export const panelAssistantFabSx = (open = false) => ({
  position: "fixed",
  right: { xs: 16, sm: 24 },
  bottom: { xs: 16, sm: 24 },
  zIndex: 1600,
  width: 56,
  height: 56,
  bgcolor: PANEL.accent.main,
  color: PANEL.accent.contrast,
  boxShadow: PANEL.accent.shadow,
  animation: open ? "none" : `${panelAssistantFabPulse} 2.4s ease-out infinite`,
  transition: "transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    bgcolor: PANEL.accent.hover,
    transform: "translateY(-2px) scale(1.04)",
    boxShadow: PANEL.accent.shadow,
  },
});

export const panelAssistantShellSx = (expanded = false) => ({
  position: "fixed",
  right: { xs: 12, sm: expanded ? 20 : 24 },
  bottom: { xs: 84, sm: 96 },
  left: expanded ? { xs: 12, sm: "auto" } : "auto",
  width: expanded
    ? { xs: "calc(100vw - 24px)", sm: "min(720px, calc(100vw - 48px))" }
    : { xs: "calc(100vw - 24px)", sm: 400 },
  maxWidth: expanded ? 760 : 440,
  height: expanded
    ? { xs: "min(78vh, 720px)", sm: "min(78vh, 680px)" }
    : { xs: "min(72vh, 580px)", sm: 540 },
  zIndex: 1600,
  display: "flex",
  flexDirection: "column",
  borderRadius: PANEL.radius.dialog,
  overflow: "hidden",
  border: `1px solid ${PANEL.border}`,
  bgcolor: PANEL.surface,
  color: PANEL.text.primary,
  backgroundImage: "none",
  boxShadow: PANEL.shadow.dialog,
  animation: `${panelAssistantPanelIn} 0.28s cubic-bezier(0.22, 1, 0.36, 1)`,
  transition: "width 0.22s ease, height 0.22s ease, max-width 0.22s ease",
  pointerEvents: "auto",
});

export const panelAssistantAccentBarSx = {
  height: 4,
  flexShrink: 0,
  background: `linear-gradient(90deg, ${PANEL.accent.main}, var(--panel-gradient-end))`,
};

export const panelAssistantHeaderSx = {
  px: 2,
  py: 1.6,
  borderBottom: `1px solid ${PANEL.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.25,
  bgcolor: PANEL.surface,
  flexShrink: 0,
};

export const panelAssistantHeaderIconWrapSx = {
  width: 40,
  height: 40,
  borderRadius: "12px",
  display: "grid",
  placeItems: "center",
  bgcolor: PANEL.accent.soft12,
  color: PANEL.accent.text,
  border: `1px solid ${PANEL.accent.softBorder22}`,
  flexShrink: 0,
};

export const panelAssistantHeaderTitleSx = {
  fontWeight: 700,
  fontSize: "0.98rem",
  color: PANEL.text.primary,
  letterSpacing: "-0.02em",
  lineHeight: 1.25,
};

export const panelAssistantHeaderSubtitleSx = {
  fontSize: "0.72rem",
  color: PANEL.text.muted,
  lineHeight: 1.35,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const panelAssistantStatusDotSx = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  bgcolor: PANEL.accent.main,
  boxShadow: `0 0 0 3px ${PANEL.accent.soft18}`,
  flexShrink: 0,
};

export const panelAssistantHeaderButtonSx = {
  color: PANEL.text.muted,
  bgcolor: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
  borderRadius: "10px",
  width: 32,
  height: 32,
  pointerEvents: "auto",
  "&:hover": {
    bgcolor: PANEL.fill.muted,
    color: PANEL.text.primary,
    borderColor: PANEL.fill.hover,
  },
};

export const panelAssistantBodySx = {
  flex: 1,
  overflowY: "auto",
  px: 1.75,
  py: 1.65,
  display: "flex",
  flexDirection: "column",
  gap: 1.2,
  bgcolor: PANEL.surface,
  backgroundImage: "none",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: PANEL.border,
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-track": { background: "transparent" },
};

export const panelAssistantHintCardSx = {
  p: 1.4,
  borderRadius: PANEL.radius.cardSm,
  border: `1px solid ${PANEL.border}`,
  bgcolor: PANEL.popover,
  boxShadow: PANEL.shadow.card,
};

/** @param {"accent"|"blue"} tone */
export const panelAssistantHintIconWrapSx = (tone = "accent") => {
  const isBlue = tone === "blue";
  return {
    width: 30,
    height: 30,
    borderRadius: "9px",
    display: "grid",
    placeItems: "center",
    color: isBlue ? PANEL.blue.text : PANEL.accent.text,
    bgcolor: isBlue ? PANEL.blue.soft12 : PANEL.accent.soft12,
    border: `1px solid ${isBlue ? PANEL.blue.soft25 : PANEL.accent.softBorder22}`,
    flexShrink: 0,
  };
};

export const panelAssistantHintTitleSx = {
  fontWeight: 600,
  fontSize: "0.84rem",
  color: PANEL.text.primary,
  letterSpacing: "-0.01em",
};

export const panelAssistantHintBodySx = {
  fontSize: "0.74rem",
  color: PANEL.text.muted,
  lineHeight: 1.55,
  pl: "38px",
};

export const panelAssistantPrivacySx = {
  px: 0.4,
  fontSize: "0.66rem",
  color: PANEL.text.label,
  lineHeight: 1.45,
};

export const panelAssistantUserBubbleSx = {
  alignSelf: "flex-end",
  maxWidth: "86%",
  px: 1.4,
  py: 1.05,
  borderRadius: "16px 16px 5px 16px",
  background: `linear-gradient(145deg, ${PANEL.accent.main} 0%, ${PANEL.accent.hover} 100%)`,
  border: `1px solid ${PANEL.accent.softBorder}`,
  boxShadow: PANEL.accent.shadow25,
  color: PANEL.accent.contrast,
  fontSize: "0.84rem",
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
};

export const panelAssistantAssistBubbleSx = {
  alignSelf: "flex-start",
  maxWidth: "94%",
  px: 1.4,
  py: 1.05,
  borderRadius: "16px 16px 16px 5px",
  bgcolor: PANEL.popover,
  border: `1px solid ${PANEL.border}`,
  boxShadow: PANEL.shadow.card,
  color: PANEL.text.body,
};

export const panelAssistantThinkingSx = {
  alignSelf: "flex-start",
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.2,
  py: 0.85,
  borderRadius: "12px",
  bgcolor: PANEL.popover,
  border: `1px solid ${PANEL.border}`,
};

export const panelAssistantThinkingTextSx = {
  fontSize: "0.74rem",
  color: PANEL.text.muted,
};

export const panelAssistantErrorSx = {
  fontSize: "0.78rem",
  color: PANEL.danger.text,
};

export const panelAssistantComposerWrapSx = {
  px: 1.4,
  py: 1.2,
  borderTop: `1px solid ${PANEL.border}`,
  bgcolor: PANEL.surface,
  flexShrink: 0,
};

export const panelAssistantComposerSx = {
  display: "flex",
  gap: 0.85,
  alignItems: "flex-end",
  p: 0.55,
  borderRadius: PANEL.radius.cardMd,
  bgcolor: PANEL.fill.muted,
  border: `1px solid ${PANEL.border}`,
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:focus-within": {
    borderColor: PANEL.accent.softBorder,
    boxShadow: `0 0 0 3px ${PANEL.accent.focusRing}`,
  },
};

export const panelAssistantInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "transparent",
    color: PANEL.text.primary,
    fontSize: "0.86rem",
    "& fieldset": { border: "none" },
    "& textarea": {
      color: PANEL.text.primary,
      WebkitTextFillColor: PANEL.text.primary,
    },
    "& textarea::placeholder": {
      color: PANEL.text.label,
      opacity: 1,
    },
  },
};

export const panelAssistantSendButtonSx = {
  bgcolor: PANEL.accent.main,
  color: PANEL.accent.contrast,
  width: 40,
  height: 40,
  mb: 0.15,
  mr: 0.15,
  flexShrink: 0,
  transition: "transform 0.15s ease, background-color 0.15s ease",
  "&:hover": {
    bgcolor: PANEL.accent.hover,
    transform: "scale(1.05)",
  },
  "&.Mui-disabled": {
    bgcolor: PANEL.accent.soft18,
    color: PANEL.text.label,
  },
};
